import {Component, Input} from '@angular/core';
import { FrontIziviaTranslateService } from '../../services/i18n/iziviaTranslate.service';
import { PricingContextDto } from '../../domain/map/chargeAreaDetailsMarker';
import {MergePricingInfo, PricingInfo, PricingInfoDetail} from '../../domain/pricing/pricingInfo';
import {GhostComponent} from '../../utils/ghostComponent';
import {find, isEqual} from 'lodash-es';

@Component({
    selector: 'pricing-context-formatter[pricingContext]',
    templateUrl: './pricingContextFormatter.component.html',
    styleUrls: ['./pricingContextFormatter.component.scss'],
})
export class PricingContextFormatterComponent extends GhostComponent {
    @Input() set pricingContext(pricingContext: PricingContextDto) {
        this.mergePricingInfos = [];
        if (pricingContext && pricingContext.pricingCodesBySubscriptionRef && pricingContext.otherPricingCodesBySubscriptionRef) {
            pricingContext = this.groupPricingCodes(pricingContext);


            // Keep only pricing related to current user subscriptions
            this.mergePricingInfos = this.createMergePricingInfos(pricingContext, pricingContext.pricingCodesBySubscriptionRef);
        }
    }

    mergePricingInfos: Array<MergePricingInfo> = [];

    constructor(private readonly translateService: FrontIziviaTranslateService) {
        super();
    }

    private createMergePricingInfos(pricingContext: PricingContextDto, pricingCodesBySubscriptionRef: { [index: string]: string[] }):
    MergePricingInfo[] {
        const unmergedPricingInfos: Array<PricingInfo> = Object.keys(pricingCodesBySubscriptionRef)
            .map(subscriptionRef => this.buildPricingInfo(pricingContext, subscriptionRef, pricingCodesBySubscriptionRef))
            .filter(pricingInfo => !!pricingInfo.lines && pricingInfo.lines.length > 0);

        // find and merge pricing infos with equal lines into a single PricingInfo (title is a comma separated list of subscription name)
        return this.mergePricingInfosWithEqualLines(unmergedPricingInfos);
    }

    private buildPricingInfo(pricingContext: PricingContextDto, subscriptionRef: string,
        pricingCodesBySubscriptionRef: { [index: string]: string[] }): PricingInfo {
        let subscriptionTitle: string = '';

        if (pricingContext.subscriptionNameByRef[subscriptionRef] === undefined
            && pricingCodesBySubscriptionRef === pricingContext.otherPricingCodesBySubscriptionRef) {
            pricingContext.subscriptionNameByRef[subscriptionRef] = this.translateService.translate('chargePoint.primoPricingInfo');
        }
        const containCurrentSubscription = !!pricingContext.pricingCodesBySubscriptionRef[subscriptionRef];

        if (pricingContext.subscriptionNameByRef && pricingContext.subscriptionNameByRef[subscriptionRef]) {
            subscriptionTitle = pricingContext.subscriptionNameByRef[subscriptionRef];
        }

        const lines: Array<string> = pricingCodesBySubscriptionRef[subscriptionRef]
            .filter((line: string) => !!line);

        return {
            containCurrentSubscription: containCurrentSubscription,
            subscriptionTitle: subscriptionTitle,
            lines: lines
        };
    }

    /**
     * Merge pricing infos containing the same description lines
     * See also issue SAM-862
     * @param unmergedPricingInfos An array of pricing infos where the title is a single subscription name.
     */
    private mergePricingInfosWithEqualLines(unmergedPricingInfos: Array<PricingInfo>): Array<MergePricingInfo> {
        const mergePricingInfos: Array<MergePricingInfo> = [];

        unmergedPricingInfos.forEach(candidate => {
            const mergePricingInfo = find(mergePricingInfos, (item: MergePricingInfo) => isEqual(item.lines, candidate.lines));
            if (mergePricingInfo) {
                if (!!candidate.containCurrentSubscription) {
                    mergePricingInfo.pricingInfoDetails.splice(0, 0, candidate as PricingInfoDetail);
                } else {
                    mergePricingInfo.pricingInfoDetails.push(candidate as PricingInfoDetail);
                }
            } else {
                mergePricingInfos.push(new MergePricingInfo(candidate as PricingInfoDetail, candidate.lines));
            }
        });
        mergePricingInfos.sort((a, b) => this.sortContainCurrentSubscription(a, b));
        return mergePricingInfos;
    }

    private sortContainCurrentSubscription(mergePricingInfos1: MergePricingInfo, mergePricingInfos2: MergePricingInfo): number {
        const containCurrentSubscription = (pricingInfoDetail: PricingInfoDetail) => !!pricingInfoDetail.containCurrentSubscription;
        if (mergePricingInfos1.pricingInfoDetails.some(containCurrentSubscription)
            && !mergePricingInfos2.pricingInfoDetails.some(containCurrentSubscription)) {
            return -1;
        } else if (!mergePricingInfos1.pricingInfoDetails.some(containCurrentSubscription)
            && mergePricingInfos2.pricingInfoDetails.some(containCurrentSubscription)) {
            return 1;
        } else {
            return 0;
        }
    }

    generateGhost(): void {
    }

    private groupPricingCodes(pricingContext: PricingContextDto): PricingContextDto {
        if (pricingContext && pricingContext.pricingCodesBySubscriptionRef && pricingContext.otherPricingCodesBySubscriptionRef) {
            Object.keys(pricingContext.pricingCodesBySubscriptionRef).forEach((currentSubscriptionRef: string) => {
                pricingContext.otherPricingCodesBySubscriptionRef[currentSubscriptionRef] = pricingContext
                    .pricingCodesBySubscriptionRef[currentSubscriptionRef];
            });
        }
        return pricingContext;
    }

    canDisplaySubscriptionName(): boolean {
        return this.mergePricingInfos.length > 1;
    }
}



